import {
  css, Global, ThemeProvider,
} from '@emotion/react';
import { lightTheme } from '~/_shared/themes/light.theme';
import type { Theme } from '~/_shared/themes/theme.model';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { ExportReportTemplateRendererComponent } from '~/exportReport/templateRenderer/exportReportTemplateRenderer.component';
import { useExportReportData } from '~/exportReport/useExportReportData';
import { useExportReportParams } from '~/exportReport/useExportReportParams';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import type { ExportReportLocationParams } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.state';

const globalStyle = (theme: Theme) => css`
  @page {
    size: A4 portrait;
    margin: 2mm;
    background: ${theme.reportColors.backgroundPrimary};
  }
  body {
    background: ${theme.reportColors.backgroundPrimary};
    color: ${theme.reportColors.textPrimary};
  }
  #root {
    overflow: visible !important;
  }
`;

/**
 * This is a ratio that allows conversion from mm to px. It was determined empirically
 */
const scaleMmToFitPage = (size: number) => `${270 / 210 * size}mm`;

const wrapperStyle = css({
  position: 'relative',
  width: scaleMmToFitPage(190),
  margin: '0 auto',
  boxSizing: 'border-box',
  paddingBottom: 20,
});

type ExportReportPrintPageProps = Readonly<{
  params: ExportReportLocationParams;
  template: ReportTemplate;
}>;

export const ExportReportLocationPrintPageComponent = ({ template, params }: ExportReportPrintPageProps) => {
  const spreadsheetData = useLatLngSpreadsheetData();
  const rowData = spreadsheetData.getRow(params.data.markerId);
  const { tableColumns } = useExportReportParams();
  const { getDemographicData, getDemographicLabel } = useExportReportData({
    template,
  });

  if (!rowData) {
    return null;
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <div css={wrapperStyle}>
        <Global styles={globalStyle} />

        {template && (
          <ExportReportTemplateRendererComponent
            template={template}
            summaryProps={{
              latLng: {
                lat: rowData.lat,
                lng: rowData.lng,
              },
              heading: params.data.summary.heading,
              semiHeading: params.data.summary.semiHeading,
              description: params.data.summary.description,
            }}
            mapProps={{
              type: ExportReportType.Location,
              center: rowData,
            }}
            tableColumns={tableColumns}
            getDemographicLabel={getDemographicLabel}
            getDemographicData={getDemographicData}
          />
        )}
      </div>
    </ThemeProvider>
  );
};
